import {
  ChevronDownIcon,
  ChevronUpIcon,
  EditIcon,
  FilterIcon,
  TrashIcon,
  UndoIcon,
  ZoomInIcon,
} from '@iconicicons/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { deepEqual } from '../../utils/objectUtils';
import { useDebouncedCallback } from '../../helpers/hooks';
import { useSelector } from 'react-redux';
import { selectAllNodes, selectAllPoints } from '../../map/slice/mapSelectors';
import { numberFormatter, priceFormatter } from '../../helpers/formatters';
import { titleize } from '../../utils/stringUtils';
import { PriceInput } from '../../helpers/PriceInput';
import { DebouncedInput } from '../../helpers/DebounceInput';
import { useFocusPoint } from '../../hooks/useFocusPoint';

/**
 * @typedef {{
 *  markets: import("../scenarioSlice").Market[];
 *  deletedMarkets: import("../scenarioSlice").Market[];
 *  onDelete: (row: import("../scenarioSlice").Market) => void;
 *  onBulkDelete: (marketIds: number[]) => void;
 *  onRestore: (ids: string[]) => void;
 *  onBulkUpdate: (ids: string[], patch: Partial<import("../scenarioSlice").Market>) => void;
 *  isEditing: boolean;
 *  onChange: (market: import('../scenarioSlice').Market) => void;
 * }}  MarketTableProps
 */

/**
 *
 * @param { MarketTableProps } props
 * @returns { React.FC< MarketTableProps> }
 */
export const MarketTable = ({
  markets = [],
  deletedMarkets = [],
  isEditing = false,
  onDelete,
  onBulkDelete,
  onRestore,
  onBulkUpdate,
  onChange: _onChange,
}) => {
  const focusPoint = useFocusPoint();
  /** @type {import('../scenarioSlice').OptimizationType} */
  const optimizationType = useSelector(
    (state) => state.scenario.data.settings.optimization_type,
  );
  const allPoints = useSelector(selectAllPoints);
  const allNodes = useSelector(selectAllNodes);
  const [filters, setFilters] = useState({
    point: '',
    pipeline: '',
    description: '',
    buy_sell: '',
    max_volume: ['', ''],
    price: ['', ''],
    price_source: '',
  });

  const lockDirection = ['ALL_PATHS_TO_POINT', 'BEST_PATHS_TO_POINT'].includes(
    optimizationType,
  );

  /**
   * @type {import('../../types/types').State<{[key: string]: boolean | undefined}>}
   */
  const [selectedMarkets, setSelectedMarkets] = useState({});
  const [showDeleted, setShowDeleted] = useState(false);

  /**
   *
   * @param {import('../scenarioSlice').Market} market
   * @param {boolean} selected
   */
  const onSelect = (market, selected) => {
    setSelectedMarkets((p) => ({
      ...p,
      [market.component_key]: selected,
    }));
  };

  useEffect(() => {
    setSelectedMarkets({});
  }, [isEditing]);

  const onChange = useCallback(
    /**
     *
     * @param {import('../scenarioSlice').Market} market
     * @param {Partial<import('../scenarioSlice').Market>} patch
     */
    (market, patch) => {
      _onChange({ ...market, ...patch });
    },
    [_onChange],
  );

  /**
   *
   * @param {import('../../services/edgeApi').Point | undefined | null} point
   */
  const onLocateMarket = (point) => {
    if (point) {
      focusPoint(point.id);
    }
  };

  const onChangeFilter = useDebouncedCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  });

  const filteredMarkets = useMemo(() => {
    if (Object.keys(allPoints).length === 0) {
      return [];
    }
    const nonNullFilters = Object.entries(filters).filter(
      ([_key, value]) => value !== null && value !== undefined,
    );
    const stringFilters = nonNullFilters.filter(
      ([key, value]) =>
        [
          'location',
          'pipeline',
          'description',
          'buy_sell',
          'price_source',
        ].includes(key) && value !== '',
    );
    const numberFilters = nonNullFilters.filter(([key, _value]) =>
      ['max_volume', 'price'].includes(key),
    );

    return markets.filter((market) => {
      const point = allPoints[market.point];
      const location = point ? `${point.id} ${point.name}` : '';
      const data = { ...market, location };

      return (
        stringFilters.every(([key, value]) => {
          return data[key]
            .trim()
            .toLowerCase()
            .includes(value.trim().toLowerCase());
        }) &&
        numberFilters.every(([key, [min, max]]) => {
          return (
            (min === '' ||
              min === null ||
              min === undefined ||
              Number(data[key]) >= min) &&
            (max === '' ||
              max === null ||
              max === undefined ||
              Number(data[key]) <= max)
          );
        })
      );
    });
  }, [markets, filters, allPoints]);

  const isEmptySelection = useMemo(
    () => Object.values(selectedMarkets).every((m) => Boolean(m) === false),
    [selectedMarkets],
  );

  if (markets.length === 0) {
    return (
      <div
        style={{
          minHeight: '200px',
          height: '100%',
        }}
        className="d-flex align-items-center justify-content-center"
      >
        <p className="text-center text-muted">No markets</p>
      </div>
    );
  }

  return (
    <table className="table">
      <thead
        style={{
          position: 'sticky',
          top: 0,
        }}
      >
        <tr>
          <th>
            Point
            <div className="d-flex align-items-center mt-2">
              <FilterIcon className="mx-3" />
              <input
                className="form-control"
                type="text"
                onChange={(evt) => onChangeFilter('location', evt.target.value)}
              />
            </div>
          </th>
          <th>
            TSP
            <div className="d-flex align-items-center mt-2">
              <input
                className="form-control"
                type="text"
                onChange={(evt) => onChangeFilter('pipeline', evt.target.value)}
              />
            </div>
          </th>
          <th>
            Description
            <div className="d-flex align-items-center mt-2">
              <input
                className="form-control"
                type="text"
                onChange={(evt) =>
                  onChangeFilter('description', evt.target.value)
                }
              />
            </div>
          </th>
          <th>
            Buy/Sell
            <div className="d-flex align-items-center mt-2">
              <select
                className="form-select"
                defaultValue=""
                style={{ width: '90px' }}
                onChange={(evt) => onChangeFilter('buy_sell', evt.target.value)}
              >
                <option value=""> </option>
                <option value="BUY">Buy</option>
                <option value="SELL">Sell</option>
                <option value="BOTH">Both</option>
              </select>
            </div>
          </th>
          <th>
            Max Volume
            <div
              className="d-flex align-items-center mt-2 input-group"
              style={{
                minWidth: '120px',
              }}
            >
              <input
                className="form-control"
                type="number"
                placeholder="Min"
                value={filters.max_volume[0]}
                onChange={(evt) =>
                  onChangeFilter('max_volume', [
                    evt.target.value,
                    filters.max_volume[1],
                  ])
                }
              />
              <input
                className="form-control"
                type="number"
                placeholder="Max"
                value={filters.max_volume[1]}
                onChange={(evt) =>
                  onChangeFilter('max_volume', [
                    filters.max_volume[0],
                    evt.target.value,
                  ])
                }
              />
            </div>
          </th>
          <th style={{ minWidth: '120px' }}>
            Market Price
            <div className="d-flex align-items-center mt-2 input-group">
              <input
                className="form-control"
                type="number"
                placeholder="Min"
                value={filters.price[0]}
                onChange={(evt) =>
                  onChangeFilter('price', [evt.target.value, filters.price[1]])
                }
              />
              <input
                className="form-control"
                type="number"
                placeholder="Max"
                value={filters.price[1]}
                onChange={(evt) =>
                  onChangeFilter('price', [filters.price[0], evt.target.value])
                }
              />
            </div>
          </th>
          <th>
            Price Source
            <div className="d-flex align-items-center mt-2">
              <input
                className="form-control"
                type="text"
                onChange={(evt) =>
                  onChangeFilter('price_source', evt.target.value)
                }
              />
            </div>
          </th>
          <th>Price Detail</th>
          {isEditing && <th>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {isEditing && (
          <>
            {showDeleted && deletedMarkets.length > 0 && (
              <tr>
                <td colSpan={9}>
                  <div
                    onClick={() => onRestore(deletedMarkets.map((m) => m.id))}
                    role="button"
                    className="d-flex align-items-center justify-content-center text-success"
                  >
                    <span>
                      Restore all <UndoIcon />
                    </span>
                  </div>
                </td>
              </tr>
            )}
            {showDeleted &&
              deletedMarkets.map((market) => {
                const point = allPoints[market.point];
                const node = allNodes[point?.node];

                return (
                  <DeletedMarketTableRow
                    key={market.id}
                    market={market}
                    point={point}
                    node={node}
                    onLocateMarket={onLocateMarket}
                    onRestore={onRestore}
                  />
                );
              })}
            {deletedMarkets.length > 0 && (
              <tr>
                <td colSpan={9}>
                  <div
                    onClick={() => setShowDeleted((p) => !p)}
                    role="button"
                    className="d-flex flex-column align-items-center justify-content-center"
                  >
                    <span>
                      {showDeleted ? 'Hide' : 'Show'} {deletedMarkets.length}{' '}
                      deleted markets
                    </span>
                    <span>
                      {showDeleted ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </span>
                  </div>
                </td>
              </tr>
            )}
            <BulkEditMarketTableRow
              onSelectAll={(checked) => {
                const selected = filteredMarkets.reduce((acc, market) => {
                  acc[market.component_key] = checked;
                  return acc;
                }, {});
                setSelectedMarkets(selected);
              }}
              allChecked={filteredMarkets.every(
                (market) => selectedMarkets[market.component_key],
              )}
              noneChecked={isEmptySelection}
              onDeleteSelected={() => {
                const selected = filteredMarkets
                  .filter((market) => selectedMarkets[market.component_key])
                  .map((market) => market.id);
                if (selected.length > 0) {
                  onBulkDelete(selected);
                  setSelectedMarkets({});
                }
              }}
              onBulkUpdate={(patch) => {
                const selection = filteredMarkets
                  .filter((market) => selectedMarkets[market.component_key])
                  .map((market) => market.id);
                onBulkUpdate(selection, patch);
              }}
            />
          </>
        )}
        {filteredMarkets.map((market) => {
          const point = allPoints[market.point];
          const node = allNodes[point?.node];
          return isEditing ? (
            <EditMarketTableRow
              key={market.component_key}
              market={market}
              point={point}
              node={node}
              onDelete={onDelete}
              onChange={onChange}
              onLocateMarket={onLocateMarket}
              onSelect={onSelect}
              isSelected={Boolean(selectedMarkets[market.component_key])}
              lockDirection={lockDirection}
            />
          ) : (
            <MarketTableRow
              key={market.component_key}
              market={market}
              point={point}
              node={node}
              onLocateMarket={onLocateMarket}
            />
          );
        })}
      </tbody>
    </table>
  );
};

/**
 *
 * @param {{
 *  market: import('../scenarioSlice').Market & {pipeline: string;};
 *  point?: import('../../services/edgeApi').Point;
 *  node: import('../../services/edgeApi').Node;
 *  onLocateMarket: (point: import('../../services/edgeApi').Point) => void;
 * }} props
 * @returns
 */
const _MarketTableRow = ({ market, point, node, onLocateMarket }) => {
  return (
    <tr>
      <td>
        <div className="d-flex align-items-center">
          <button
            className="btn btn-light me-2"
            disabled={!point}
            style={{
              opacity: 1,
            }}
            onClick={() => onLocateMarket(point)}
          >
            <ZoomInIcon />
          </button>
          <span>
            <strong className="me-2">#{market.point}</strong> {point?.name}
          </span>
        </div>
      </td>
      <td>{market.pipeline}</td>
      <td>{market.description}</td>
      <td className="text-center">{market.buy_sell}</td>
      <td className="text-center">{numberFormatter(market.max_volume)}</td>
      <td className="text-center">{priceFormatter.format(market.price)}</td>
      <td className="text-center">
        {titleize(market.price_source?.split('_').join(' '))}
      </td>
      <td>{market.price_explanation}</td>
    </tr>
  );
};

/**
 *
 * @param {{
 *  market: import('../scenarioSlice').Market & {pipeline: string;};
 *  point?: import('../../services/edgeApi').Point;
 *  node: import('../../services/edgeApi').Node;
 *  onLocateMarket: (point: import('../../services/edgeApi').Point) => void;
 *  onRestore: (ids: string[]) => void;
 * }} props
 * @returns
 */
const _DeletedMarketTableRow = ({
  market,
  point,
  node,
  onLocateMarket,
  onRestore,
}) => {
  return (
    <tr className="table-danger">
      <td>
        <div className="d-flex align-items-center">
          <button
            className="btn btn-light me-2"
            disabled={!node}
            style={{
              opacity: 1,
            }}
            onClick={() => onLocateMarket(point)}
          >
            <ZoomInIcon />
          </button>
          <span>
            <strong className="me-2">#{market.point}</strong> {point?.name}
          </span>
        </div>
      </td>
      <td>{market.pipeline}</td>
      <td>{market.description}</td>
      <td className="text-center">{market.buy_sell}</td>
      <td className="text-center">{numberFormatter(market.max_volume)}</td>
      <td className="text-center">{priceFormatter.format(market.price)}</td>
      <td className="text-center">
        {titleize(market.price_source?.split('_').join(' '))}
      </td>
      <td>{market.price_explanation}</td>
      <td
        className="text-center"
        role="button"
        onClick={() => onRestore([market.id])}
      >
        <UndoIcon color="green" />
      </td>
    </tr>
  );
};

/**
 *
 * @param {{
 *  market: import('../scenarioSlice').Market & {pipeline: string;};
 *  point: import('../../services/edgeApi').Point;
 *  node: import('../../services/edgeApi').Node;
 *  onDelete: (row: import('../scenarioSlice').Market) => void;
 *  onChange: (market: import('../scenarioSlice').Market, patch: Partial<import('../scenarioSlice').Market>) => void;
 *  onLocateMarket: (point: import('../../services/edgeApi').Point) => void;
 *  onSelect: (market: import('../scenarioSlice').Market, selected: boolean) => void;
 *  isSelected: boolean;
 *  lockDirection: boolean;
 * }} props
 * @returns
 */
const _EditMarketTableRow = ({
  market,
  point,
  node,
  onLocateMarket,
  onDelete,
  onChange,
  onSelect,
  isSelected,
  lockDirection,
}) => {
  return (
    <tr className={isSelected ? 'table-secondary' : ''}>
      <td>
        <div className="d-flex align-items-center">
          <div
            style={{ width: '30px' }}
            onClick={() => onSelect(market, !isSelected)}
          >
            <input
              type="checkbox"
              readOnly
              checked={isSelected}
              className="form-check-input form-check-input-sm me-2"
            />
          </div>
          <button
            className="btn btn-light me-2"
            disabled={!node}
            style={{
              opacity: 1,
            }}
            onClick={() => onLocateMarket(point)}
          >
            <ZoomInIcon />
          </button>
          <span onClick={() => onSelect(market, !isSelected)} role="button">
            <strong className="me-2">#{market.point}</strong> {point?.name}
          </span>
        </div>
      </td>
      <td onClick={() => onSelect(market, !isSelected)} role="button">
        {market.pipeline}
      </td>
      <td>
        <DebouncedInput
          textArea
          className="form-control form-control-sm"
          value={market.description}
          onChange={(description) =>
            onChange(market, {
              description,
            })
          }
        />
      </td>
      <td className="text-center">
        <select
          disabled={lockDirection}
          type="select"
          className="form-select form-select-sm"
          value={market.buy_sell}
          style={{ width: '90px' }}
          onChange={(e) => onChange(market, { buy_sell: e.target.value })}
        >
          <option value="BUY">Buy</option>
          <option value="SELL">Sell</option>
          <option value="BOTH">Both</option>
        </select>
      </td>
      <td className="text-center">
        <DebouncedInput
          type="number"
          className="form-control form-control-sm"
          value={market.max_volume}
          onChange={(max_volume) => onChange(market, { max_volume })}
          min={0}
        />
      </td>
      <td className="text-center">
        <PriceInput price={market} onChange={onChange} />
      </td>
      <td className="text-center">
        {titleize(market.price_source?.split('_').join(' '))}
      </td>
      <td>{market.price_explanation}</td>
      <td className="text-center">
        <TrashIcon
          onClick={() => onDelete(market.id)}
          role="button"
          color="red"
        />
      </td>
    </tr>
  );
};

/**
 *
 * @param {{
 *  allChecked: boolean;
 *  noneChecked: boolean;
 *  onSelectAll: (selected: boolean) => void;
 *  onDeleteSelected: () => void;
 *  onBulkUpdate: (patch: Partial<import('../scenarioSlice').Market>) => void;
 * }} props
 * @returns
 */
const BulkEditMarketTableRow = ({
  allChecked,
  noneChecked,
  onSelectAll,
  onDeleteSelected,
  onBulkUpdate,
}) => {
  /**
   * @type {import('../../types/types').State<Partial<import('../scenarioSlice').Market>}
   */
  const [patch, setPatch] = useState({
    description: '',
    buy_sell: '',
    max_volume: 0,
    price: 0,
    base_price: 0,
    adder: 0,
    price_component: null,
    price_component_description: '',
  });
  return (
    <tr>
      <td colSpan={2}>
        <div className="d-flex align-items-center">
          <input
            type="checkbox"
            readOnly
            checked={allChecked}
            onChange={(e) => onSelectAll(e.target.checked)}
            className="form-check-input form-check-input-sm me-3"
          />
          <button
            className={`btn ${noneChecked ? 'btn-secondary' : 'btn-danger'}`}
            style={{ opacity: 1 }}
            disabled={noneChecked}
            onClick={onDeleteSelected}
          >
            Delete Selected
          </button>
        </div>
      </td>
      {noneChecked ? (
        <td colSpan={7}></td>
      ) : (
        <>
          <td>
            <input
              type="text"
              className="form-control form-control-sm"
              value={patch.description}
              onChange={(evt) => {
                setPatch((p) => ({ ...p, description: evt.target.value }));
              }}
            />
          </td>
          <td className="text-center">
            <select
              type="select"
              className="form-select form-select-sm"
              style={{ width: '90px' }}
              value={patch.buy_sell}
              onChange={(evt) => {
                setPatch((p) => ({ ...p, buy_sell: evt.target.value }));
              }}
            >
              <option value="">Select</option>
              <option value="BUY">Buy</option>
              <option value="SELL">Sell</option>
              <option value="BOTH">Both</option>
            </select>
          </td>
          <td className="text-center">
            <input
              type="number"
              className="form-control form-control-sm"
              min={0}
              value={patch.max_volume}
              onChange={(evt) => {
                setPatch((p) => ({ ...p, max_volume: evt.target.value }));
              }}
            />
          </td>
          <td className="text-center">
            <PriceInput
              price={patch}
              onChange={(_price, pricePatch) => {
                setPatch((p) => ({ ...p, ...pricePatch }));
              }}
            />
          </td>
          <td className="text-center" colSpan={2}></td>
          <td className="text-center">
            <EditIcon
              className="text-primary"
              onClick={() =>
                onBulkUpdate(
                  Object.fromEntries(
                    Object.entries(patch).filter(([_, v]) => Boolean(v)),
                  ),
                )
              }
              role="button"
            />
          </td>
        </>
      )}
    </tr>
  );
};

const MarketTableRow = React.memo(_MarketTableRow, (prev, next) => {
  return deepEqual(prev.market, next.market);
});

const DeletedMarketTableRow = React.memo(
  _DeletedMarketTableRow,
  (prev, next) => {
    return deepEqual(prev.market, next.market);
  },
);

const EditMarketTableRow = React.memo(_EditMarketTableRow, (prev, next) => {
  return (
    deepEqual(prev.market, next.market) && prev.isSelected === next.isSelected
  );
});
